import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import { useBusinesses } from "@/hooks/business/useBusinesses";
import { usePortfolioData } from "@/hooks/franchise/usePortfolioData";
import { useDashboardTab } from "@/context/DashboardTabContext";

// Import franchise components
import PortfolioHealthCard from "@/components/franchise/PortfolioHealthCard";
import VenueStatusGrid from "@/components/franchise/VenueStatusGrid";
import AlertBanner from "@/components/franchise/AlertBanner";
import CustomSolutionsTab from "@/components/franchise/CustomSolutionsTab";

const FranchiseDashboard = () => {
  const navigate = useNavigate();
  const { franchiseDashboardTab: activeView } = useDashboardTab();

  const {
    businesses,
    loadingBusinesses,
    selectedBusinessId,
    setSelectedBusinessId,
  } = useBusinesses();

  const { venues, totals, loading: portfolioLoading, error } = usePortfolioData();

  // Navigate to specific venue dashboard
  const handleVenueClick = (businessId: string) => {
    setSelectedBusinessId(businessId);
    navigate('/dashboard');
  };

  // Render content based on active view
  const renderContent = () => {
    switch (activeView) {
      case "overview":
        return (
          <div className="space-y-6">
            {/* Alert Banner */}
            <AlertBanner venues={venues} />

            {/* Portfolio Health Stats */}
            <PortfolioHealthCard totals={totals} loading={portfolioLoading} />

            {/* Venue Status Grid */}
            <VenueStatusGrid
              venues={venues}
              loading={portfolioLoading}
              onVenueClick={handleVenueClick}
            />
          </div>
        );

      case "venues":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">All Venues</h2>
            <VenueStatusGrid
              venues={venues}
              loading={portfolioLoading}
              onVenueClick={handleVenueClick}
              showDetails
            />
          </div>
        );

      case "schedule":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Portfolio Schedule</h2>
            <p className="text-muted-foreground">Multi-venue schedule overview coming soon...</p>
          </div>
        );

      case "analytics":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Portfolio Analytics</h2>
            <p className="text-muted-foreground">Cross-venue analytics coming soon...</p>
          </div>
        );

      case "payroll":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Consolidated Payroll</h2>
            <p className="text-muted-foreground">Portfolio-wide payroll summary coming soon...</p>
          </div>
        );

      case "alerts":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Alert Center</h2>
            <AlertBanner venues={venues} showAll />
          </div>
        );

      case "settings":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">Portfolio Settings</h2>
            <p className="text-muted-foreground">Portfolio-level settings coming soon...</p>
          </div>
        );

      case "custom-solutions":
        return <CustomSolutionsTab />;

      default:
        return null;
    }
  };

  // Note: loadingBusinesses is handled by SubscriptionRequiredRoute
  // Only portfolioLoading needs to be handled here for portfolio-specific data

  return (
    <>
      {businesses.length === 0 ? (
        <div className="mt-12 text-center bg-white dark:bg-card p-8 rounded-lg shadow">
          <Building2 className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500" />
          <h2 className="mt-6 text-2xl font-semibold dark:text-gray-100">No Venues Yet</h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Create your first venue to start managing your portfolio.
          </p>
        </div>
      ) : businesses.length === 1 ? (
        <div className="mt-12 text-center bg-white dark:bg-card p-8 rounded-lg shadow">
          <Building2 className="w-16 h-16 mx-auto text-blue-500" />
          <h2 className="mt-6 text-2xl font-semibold dark:text-gray-100">Single Venue Mode</h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Portfolio view is designed for managing multiple venues.
            Add more venues to unlock portfolio management features.
          </p>
        </div>
      ) : error ? (
        <div className="mt-12 text-center bg-red-50 dark:bg-red-950/20 p-8 rounded-lg">
          <h2 className="text-xl font-semibold text-red-600">Error Loading Portfolio</h2>
          <p className="mt-2 text-red-500">{error}</p>
        </div>
      ) : (
        renderContent()
      )}
    </>
  );
};

export default FranchiseDashboard;
